import { memo, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgress } from '@material-ui/core';
import { RoutePath } from 'consts/RoutePath';
import { HttpStatus } from 'consts/HttpStatus';
import { callAttributeAgreeApi, callGetAttributeAgreeApi, GetAttributeAgreeResData } from 'requests/RequestUser';
import { callCheckWhiteListApi } from 'requests/RequestWhiteList';
import {
	GetLoginDisplayItemResData,
	callGetLoginDisplayItemApi,
	CommonLoginV2ResData,
	callCommonLoginV2AfterAttributeAgreeApi,
} from 'requests/RequestLogin';
import { GetSiteResData, callGetSiteApi } from 'requests/RequestSite';
import { useReceiveV2AuthorizeRequest } from 'hooks/UseReceiveV2AuthorizeRequest';
import { ResponseData } from 'hooks/UseNext4To5';
import { convertObjectToGetParameter } from 'common/ObjectToGetParameterConverter';
import classes from './override.module.scss';

type Props = {};
interface KeyString {
	[key: string]: string[];
}
const NOTES: KeyString = {
	email: ['メールアドレス'],
	profile: ['お名前', '生年月日', '性別', 'ニックネーム'],
	address: ['郵便番号', 'ご住所（都道府県/市区町村/町名・番地/マンション名）', '国'],
	phone: ['電話番号'],
};

export const AttributeAgreementTemplate = memo((_props: Props) => {
	const history = useHistory<ResponseData>();
	const dto = history.location.state?.v2;
	const { v2Params, isV2ParamsOk } = useReceiveV2AuthorizeRequest();
	const [formData, setFormData] = useState({ attribute_agree: 0 });
	const isRegister = v2Params.gid_act === 'register';

	const [isCompleteButtonDisabled, setIsCompleteButtonDisabled] = useState(true);
	const handleChangeCheckValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, [event.target.name]: Number(event.target.checked) }));
		setIsCompleteButtonDisabled(!event.target.checked);
	};
	const [displayItem, setDisplayItem] = useState<GetLoginDisplayItemResData>(undefined);
	const [site, setSite] = useState<GetSiteResData>(undefined);
	const [loading, setLoading] = useState(false);
	const complete = async () => {
		setLoading(true);

		const updateAttributeAgreeResult = await callAttributeAgreeApi(v2Params.client_id, v2Params.scope);
		if (updateAttributeAgreeResult.status !== HttpStatus.OK) {
			history.push(RoutePath.ERROR);
			setLoading(false);
			return;
		}

		let getParameters = '';
		if (isRegister && dto !== undefined) {
			getParameters = convertObjectToGetParameter(dto);
		} else {
			const commonLoginV2Result = await callCommonLoginV2AfterAttributeAgreeApi(v2Params);
			if (commonLoginV2Result.status !== HttpStatus.OK) {
				history.push(RoutePath.ERROR);
				setLoading(false);
				return;
			}
			const commonLoginV2ResultData = commonLoginV2Result.data as CommonLoginV2ResData;
			getParameters = convertObjectToGetParameter(commonLoginV2ResultData);
		}

		window.location.href = `${
			v2Params.redirect_uri ? decodeURIComponent(v2Params.redirect_uri) : displayItem.redirect_url
		}?${getParameters}`;
	};

	const [notes, setNotes] = useState<string[]>([]);
	useEffect(() => {
		if (!isV2ParamsOk) {
			return;
		}
		const execute = async () => {
			setLoading(true);

			const results = await Promise.all([
				callCheckWhiteListApi({
					burl: v2Params.redirect_uri,
					scd: v2Params.client_id,
				}),
				callGetLoginDisplayItemApi({
					gid_dp: v2Params.gid_dp,
					site_cd: v2Params.client_id,
				}),
				callGetSiteApi(v2Params.client_id),
				callGetAttributeAgreeApi(v2Params.client_id, v2Params.scope),
			]);

			const isError = results.some((r) => r.status !== HttpStatus.OK);
			if (isError) {
				history.push(RoutePath.ERROR);
				setLoading(false);
				return;
			}

			const [_whiteListCheckResult, getDisplayItemResult, getSiteResult, getAttributeAgreeResult] = results;
			setDisplayItem(getDisplayItemResult.data);
			const siteData = getSiteResult.data;
			setSite(siteData);
			const attributeAgreeData = getAttributeAgreeResult.data as GetAttributeAgreeResData;
			const scopeNotes = getAttributeAgreeResult.data.disagreement_scope?.split(' ').reduce((acc, v) => {
				return [...acc, ...(NOTES[v] != undefined ? NOTES[v] : [])];
			}, []);
			setNotes(scopeNotes);

			setLoading(false);
		};
		execute();
	}, [isV2ParamsOk]);

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenID属性提供同意';
	}, []);

	return (
		<>
			<section className="section">
				<div className="section-in" ref={scrollRef}>
					<div className="section-head">
						{site?.claim_agreement_title ? site?.claim_agreement_title : 'サービスに送信する情報'}
					</div>
					{!!v2Params?.response_type && !!v2Params?.client_id ? (
						<>
							<div className="section-txt-box">
								<div className={classes.attributeAgreementDescription}>
									{site?.claim_agreement_description
										? site?.claim_agreement_description
										: '本サービスをご利用いただくにあたり以下のユーザー情報がサービスに対して送信されます。'}
								</div>
								<ul>
									{notes.map((n) => (
										<li>{n}</li>
									))}
								</ul>
							</div>

							<div className="form-box">
								<div className="form-input">
									<div className="check-box">
										<ul>
											<li>
												<label
													className={`check-label ${formData.attribute_agree === 1 ? 'is-active' : ''}`}
													htmlFor="attribute_agree"
												>
													<input
														type="checkbox"
														id="attribute_agree"
														name="attribute_agree"
														checked={formData.attribute_agree === 1}
														onChange={handleChangeCheckValue}
													/>
													上記を確認の上同意する
												</label>
											</li>
										</ul>
									</div>
								</div>
							</div>

							<div className="btn-box">
								<div className="btn gr">
									{loading ? (
										<CircularProgress />
									) : (
										<button
											className={isCompleteButtonDisabled ? classes.disabled : ''}
											onClick={complete}
											disabled={isCompleteButtonDisabled}
										>
											{site?.claim_agreement_button_display_complete
												? `${site?.claim_agreement_button_display_complete}`
												: '許可する'}
										</button>
									)}
								</div>
							</div>
						</>
					) : (
						<div className="error-box">
							<div className="error-box-in">
								<p>必要なパラメータが存在しません。</p>
							</div>
						</div>
					)}
				</div>
			</section>
		</>
	);
});
